// Client Portal — VoiceIQ (VOIP call center) portal. Extracted from App.js.
import { useState } from "react";
import { PlayCircle, Radio, Settings, Sparkles } from "lucide-react";
import { KB_ROLE_LEVELS } from "./RAGChat";
import { SystemBanner } from "../components/SystemBanner";

const VOIPPanel = ({ facility, user, kbRole }) => {
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const isAdmin = kbRoleGte("admin");
  const [subTab, setSubTab] = useState("live");

  const subTabs = [
    { id: "live", label: "Live", icon: Radio },
    { id: "calls", label: "Calls", icon: PlayCircle },
    { id: "kb-suggestions", label: "KB Suggestions", icon: Sparkles },
    ...(isAdmin
      ? [{ id: "agent-config", label: "Agent Config", icon: Settings }]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              subTab === t.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "live" && <VOIPLive facility={facility} />}
      {subTab === "calls" && <VOIPCalls facility={facility} />}
      {subTab === "kb-suggestions" && <VOIPKBSuggestions facility={facility} />}
      {subTab === "agent-config" && isAdmin && (
        <VOIPAgentConfig facility={facility} user={user} />
      )}
    </div>
  );
};

export const VoiceIQPortal = ({
  facility,
  user,
  systemMessages = [],
  onDismissMessage,
}) => {
  const svc = facility.services?.find((s) => s.service_type === "voip");
  const kbRole = svc?.access_role || "viewer";
  return (
    <div className="space-y-4">
      {systemMessages.map((m) => (
        <SystemBanner key={m.id} message={m} onDismiss={onDismissMessage} />
      ))}
      <VOIPPanel facility={facility} user={user} kbRole={kbRole} />
    </div>
  );
};

// ── Topics Management ───────────────────────────────────────────────
const VOIPStubCard = ({ icon: Icon, title, description, children }) => (
  <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
    <div className="flex items-center mb-2">
      <Icon className="w-5 h-5 text-blue-600 mr-2" />
      <h3 className="text-lg font-bold text-slate-900">{title}</h3>
    </div>
    <p className="text-sm text-slate-600 mb-4">{description}</p>
    {children}
  </div>
);

const VOIPLive = ({ facility }) => (
  <VOIPStubCard
    icon={Radio}
    title="Live Call Console"
    description={`Real-time transcript and KB hits for active calls at ${facility.display_name}. Mirrors the Chat layout with speaker-labeled turns.`}
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
      <div className="bg-slate-50 rounded p-3">
        <div className="text-slate-500 text-xs mb-1">Active Calls</div>
        <div className="text-2xl font-bold text-slate-900">0</div>
      </div>
      <div className="bg-slate-50 rounded p-3">
        <div className="text-slate-500 text-xs mb-1">Phone Number</div>
        <div className="font-mono text-slate-900">—</div>
      </div>
      <div className="bg-slate-50 rounded p-3">
        <div className="text-slate-500 text-xs mb-1">Avg Sentiment</div>
        <div className="font-semibold text-slate-900">—</div>
      </div>
    </div>
    <div className="mt-4 p-4 bg-slate-50 rounded border border-dashed border-slate-300 text-center text-sm text-slate-500">
      Live transcript feed appears here when a call is in progress.
    </div>
  </VOIPStubCard>
);

const VOIPCalls = ({ facility }) => (
  <VOIPStubCard
    icon={PlayCircle}
    title="Call Log"
    description={`Past calls for ${facility.display_name}. Row click opens transcript, summary, sentiment, and KB hit details.`}
  >
    <div className="overflow-hidden border border-slate-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            <th className="px-4 py-2 text-left">Date</th>
            <th className="px-4 py-2 text-left">Caller</th>
            <th className="px-4 py-2 text-left">Duration</th>
            <th className="px-4 py-2 text-left">Resolution</th>
            <th className="px-4 py-2 text-left">Sentiment</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
              No calls yet. Connects to <code>calls</code> +{" "}
              <code>call_summaries</code> tables.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </VOIPStubCard>
);

const VOIPKBSuggestions = ({ facility }) => (
  <VOIPStubCard
    icon={Sparkles}
    title="KB Suggestions"
    description={`Unanswered caller questions surfaced from calls at ${facility.display_name}. Approve to embed directly into this facility's knowledge base.`}
  >
    <div className="p-4 bg-slate-50 rounded border border-dashed border-slate-300 text-center text-sm text-slate-500">
      Queue of <code>kb_suggestions</code> rows. Same approve/deny flow as Gap
      Tasks — accepted items become qa_pairs in the shared KB.
    </div>
  </VOIPStubCard>
);

const VOIPAgentConfig = ({ facility }) => (
  <VOIPStubCard
    icon={Settings}
    title="Agent Config"
    description="Voice, greeting, filler phrases, and call routing for this facility's VOIP agent."
  >
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
      {[
        { label: "Voice", value: "— (select)" },
        { label: "Greeting", value: "—" },
        { label: "Max Call Duration", value: "10 min" },
        { label: "Filler Phrase Set", value: "default" },
      ].map((item) => (
        <div
          key={item.label}
          className="flex justify-between bg-slate-50 rounded p-3"
        >
          <span className="text-slate-500">{item.label}</span>
          <span className="font-medium text-slate-900">{item.value}</span>
        </div>
      ))}
    </div>
  </VOIPStubCard>
);

// ── RAG Customize — theme, prompts, embed code ──────────────────
